import { 
    LambdaClient, 
    ListFunctionsCommand 
} from '@aws-sdk/client-lambda';
import { fetchLogGroups, createLogGroupRetentionMap, fetchLambdaInvocationMetrics, fetchLambda30DayMetrics } from './cloudWatchClient';

export const fetchLambdaFunctions = async (credentials, region, invocationScanOptions = {}) => {
  const client = new LambdaClient({
    region,
    credentials: {
      accessKeyId: credentials.accessKeyId,
      secretAccessKey: credentials.secretAccessKey,
      sessionToken: credentials.sessionToken,
    },
  });

  const allFunctions = [];
  let nextMarker = undefined;

  do {
    const command = new ListFunctionsCommand({
      MaxItems: 50, // Lambda uses MaxItems instead of MaxResults
      Marker: nextMarker,
    });

    const response = await client.send(command);
    
    if (response.Functions) {
      allFunctions.push(...response.Functions);
    }
    
    nextMarker = response.NextMarker;
  } while (nextMarker);

  // Extract data options (default to true for backward compatibility)
  const dataOptions = invocationScanOptions.dataOptions || {
    includeRuntimeDeprecation: true,
    includeLogRetention: true,
    includeLastInvocation: true,
    includeInvocationCounts: true
  };

  // Fetch log group retention data if enabled
  let logGroupRetentionMap = new Map();
  if (dataOptions.includeLogRetention) {
    try {
      const logGroups = await fetchLogGroups(credentials, region);
      logGroupRetentionMap = createLogGroupRetentionMap(logGroups);
    } catch (error) {
      console.warn('Failed to fetch log groups:', error.message);
    }
  }

  // Fetch invocation metrics if enabled
  let invocationMetricsMap = new Map();
  if (dataOptions.includeLastInvocation) {
    try {
      const functionNames = allFunctions.map(func => func.FunctionName);
      
      // Default smart tiered scanning options (180-day default with intelligent tiers)
      const scanOptions = {
        quickScanDays: 30,           // Recent activity (fast)
        mediumScanDays: 180,         // 6-month comprehensive scan (default)
        deepScanDays: 455,           // Maximum CloudWatch retention (~15 months)
        enableSmartTiering: true,    // Use tiered approach
        enableEarlyExit: true,       // Stop early if enough functions found
        earlyExitThreshold: 0.8,     // Stop if 80% of functions have data
        enableDebugLogging: false,   // Set to true for detailed logs
        ...invocationScanOptions     // Allow override of defaults
      };
      
      invocationMetricsMap = await fetchLambdaInvocationMetrics(credentials, region, functionNames, scanOptions);
    } catch (error) {
      console.warn('Failed to fetch invocation metrics:', error.message);
    }
  }

  // Fetch 30-day metrics if enabled
  let thirtyDayMetricsMap = new Map();
  if (dataOptions.includeInvocationCounts) {
    try {
      const functionNames = allFunctions.map(func => func.FunctionName);
      thirtyDayMetricsMap = await fetchLambda30DayMetrics(credentials, region, functionNames);
    } catch (error) {
      console.warn('Failed to fetch 30-day metrics:', error.message);
    }
  }

  // Enhance each function with collected data
  allFunctions.forEach(func => {
    // Add log retention data
    if (dataOptions.includeLogRetention) {
      // Lambda log group naming convention: /aws/lambda/{functionName}
      const logGroupName = `/aws/lambda/${func.FunctionName}`;
      const logGroupData = logGroupRetentionMap.get(logGroupName);
      
      if (logGroupData) {
        func.LogGroupRetention = logGroupData.retentionDisplay;
        func.LogGroupRetentionDays = logGroupData.retentionInDays;
        func.LogGroupArn = logGroupData.logGroupArn;
        func.LogGroupStoredBytes = logGroupData.storedBytes;
      } else {
        // Log group might not exist yet (function never invoked) or naming doesn't match convention
        func.LogGroupRetention = 'Log Group Not Found';
        func.LogGroupRetentionDays = null;
        func.LogGroupArn = '';
        func.LogGroupStoredBytes = 0;
      }
    } else {
      // Set default values when log retention is disabled
      func.LogGroupRetention = 'Not Collected';
      func.LogGroupRetentionDays = null;
      func.LogGroupArn = '';
      func.LogGroupStoredBytes = 0;
    }

    // Add invocation data
    if (dataOptions.includeLastInvocation) {
      const invocationData = invocationMetricsMap.get(func.FunctionName);
      if (invocationData) {
        func.LastInvocation = invocationData.lastInvocation;
        func.LastInvocationDisplay = invocationData.lastInvocationDisplay;
        func.MaxDaysScanned = invocationData.maxDaysScanned; // Add scan depth context
      } else {
        func.LastInvocation = null;
        func.LastInvocationDisplay = 'Unknown';
        func.MaxDaysScanned = 0;
      }
    } else {
      // Set default values when invocation data is disabled
      func.LastInvocation = null;
      func.LastInvocationDisplay = 'Not Collected';
      func.MaxDaysScanned = 0;
    }

    // Add 30-day metrics data
    if (dataOptions.includeInvocationCounts) {
      const thirtyDayData = thirtyDayMetricsMap.get(func.FunctionName);
      if (thirtyDayData) {
        func.Invocations30Days = thirtyDayData.invocations30Days;
        func.Errors30Days = thirtyDayData.errors30Days;
      } else {
        func.Invocations30Days = 0;
        func.Errors30Days = 0;
      }
    } else {
      // Set default values when 30-day metrics are disabled
      func.Invocations30Days = 'Not Collected';
      func.Errors30Days = 'Not Collected';
    }

    // Store data options for downstream processing
    func._dataOptions = dataOptions;
  });

  return allFunctions;
};

// Export helper function for custom scanning configurations
export const createInvocationScanConfig = {
  // Quick scan for recently active functions only (fast)
  recentOnly: () => ({
    quickScanDays: 30,
    mediumScanDays: 30,  // Same as quick to skip medium scan
    deepScanDays: 30,    // Same to skip deep scan
    enableSmartTiering: false,
    enableDebugLogging: false
  }),
  
  // Comprehensive scan up to maximum CloudWatch retention (slow but complete)
  comprehensive: () => ({
    quickScanDays: 30,
    mediumScanDays: 180,
    deepScanDays: 455,   // Maximum possible
    enableSmartTiering: true,
    enableEarlyExit: false,  // Don't exit early, scan everything
    enableDebugLogging: true
  }),
  
  // Balanced approach (default - good for most use cases)
  balanced: () => ({
    quickScanDays: 30,
    mediumScanDays: 180,  // 6 months
    deepScanDays: 365,    // 1 year
    enableSmartTiering: true,
    enableEarlyExit: true,
    earlyExitThreshold: 0.8,
    enableDebugLogging: false
  }),
  
  // Custom configuration
  custom: (options) => options
};